import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { usersAPI } from '../../api';
import { apiError, passwordProblem } from './usersUtils';

/** Give someone a new password. */
const PasswordModal = ({ isOpen, onClose, person, onSaved }) => {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(true);
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setShow(true);
      setProblem('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = passwordProblem(password);
    if (found) {
      setProblem(found);
      return;
    }
    setSaving(true);
    try {
      await usersAPI.setPassword(person.id, password);
      onSaved(person);
    } catch (error) {
      setProblem(apiError(error, 'Could not save the password. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (!person) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`New password for ${person.full_name || person.username}`} size="sm">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
            New password
          </label>
          <div className="relative">
            <input
              id="new-password"
              autoFocus
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setProblem('');
              }}
              autoComplete="new-password"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
              aria-label={show ? 'Hide password' : 'Show password'}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {problem ? (
            <p className="mt-1 text-sm text-red-600">{problem}</p>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              At least 6 characters. Tell {person.full_name || person.username} the new password.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save password'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PasswordModal;
